import { ChevronRight } from 'lucide-react';
import { ReactComponent as SvgSnowflake } from 'public/assets/icons/snowflakeFlat.svg';
import { ReactComponent as SvgMarker } from 'public/assets/icons/marker.svg';
import { cn } from '@/util/cn';
import { ImageComponent } from '@/components/common/ImageComponent';
import { IFeaturedResort } from '@/api/home/types';
import { IResortPathInfo, IDescriptionsResort } from '@/api/resort/types';
import { Button } from '@/components/ui/button';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { Routes } from '@/routes';

// Featured-resort card used on the home page and 7 region pages.
// Layout: image-with-snowflake-decoration column + descriptive content
// column, swap order at xl+. The `isOnRegionSkiResorts` modifier expands
// into the sidebar gutter on region ski-resorts pages — relies on the
// `--sidebar-width` / `--sidebar-gap` CSS vars (Layout currently
// doesn't publish those; Tier 7 follow-up will restore the calc).

const sectionBaseClass = [
  'overflow-hidden',
  'lg:mb-[4.75rem]',
  'xl:flex xl:flex-row-reverse xl:items-center xl:gap-[95px] xl:overflow-visible',
  '[&>div]:relative xl:[&>div]:flex-1',
].join(' ');

const sectionRegionSkiResortsClass = [
  'p-0',
  'lg:w-[calc(100%+var(--sidebar-width,0px)+var(--sidebar-gap,0px))]',
  'lg:mb-12',
  'max-xl:px-12 max-xl:pt-4',
  'max-sm:px-5 max-sm:pt-4',
].join(' ');

const sectionRegionExceptSkiResortsClass = [
  'p-0 pt-8',
  'max-xl:px-12',
  'max-sm:px-5',
  '[&>div>div:first-child>img]:!aspect-[1.338]',
  'xl:[&>div>div:first-child>img]:!min-h-[22.1875rem] xl:[&>div>div:first-child>img]:!min-w-[29.6875rem]',
  '[&>div>.snowflake>svg]:right-[-45%] [&>div>.snowflake>svg]:top-[75%]',
  'sm:[&>div>.snowflake>svg]:right-[-8%] sm:[&>div>.snowflake>svg]:top-[70%]',
  'xl:[&>div>.snowflake>svg]:!w-56 xl:[&>div>.snowflake>svg]:!h-56',
  'xl:[&>div>.snowflake>svg]:!right-[90%] xl:[&>div>.snowflake>svg]:!top-[-15%]',
].join(' ');

const imageWrapClass = [
  'relative z-[1]',
  '[&>img]:w-full [&>img]:object-cover [&>img]:rounded-[20px] [&>img]:aspect-[1.657]',
].join(' ');

const partnerBadgeClass = [
  'absolute top-5 right-0 z-[2]',
  'bg-secondary py-1.5 pl-6 pr-4 flex items-center',
  '[clip-path:polygon(0_0,100%_0,100%_100%,0_100%,7%_50%)]',
  'max-md:py-1 max-md:pl-5 max-md:pr-3',
  '[&>p]:text-white [&>p]:font-medium [&>p]:text-sm [&>p]:leading-5',
  '[&>p]:tracking-[0.07em] [&>p]:m-0 [&>p]:uppercase [&>p]:whitespace-nowrap',
  'max-xl:[&>p]:text-xs max-xl:[&>p]:leading-4',
  'max-md:[&>p]:text-[10px] max-md:[&>p]:leading-[14px]',
].join(' ');

const snowflakeClass = [
  'snowflake',
  '[&>svg]:absolute [&>svg]:w-[25.4375rem] [&>svg]:h-[24.375rem]',
  '[&>svg]:top-[67%] [&>svg]:right-[-55%] [&>svg]:z-0',
  'sm:[&>svg]:w-[36%] sm:[&>svg]:top-[75%] sm:[&>svg]:right-[-15%]',
  'xl:[&>svg]:w-[25.4375rem] xl:[&>svg]:h-[24.375rem]',
  'xl:[&>svg]:right-[-55%] xl:[&>svg]:top-[-5%]',
].join(' ');

const contentClass = [
  'my-7 max-w-[43.75rem]',
  '[&>h6]:font-semibold [&>h6]:text-xs [&>h6]:tracking-wider [&>h6]:uppercase',
  '[&>h6]:text-muted-foreground [&>h6]:mb-4',
  '[&>h3]:font-heading [&>h3]:font-semibold [&>h3]:text-2xl [&>h3]:capitalize',
  '[&>h3]:text-secondary [&>h3]:mb-3',
  'xl:[&>h3]:text-3xl xl:[&>h3]:mb-4',
  '[&>p]:text-base [&>p]:leading-relaxed [&>p]:text-foreground/85 [&>p]:my-6',
].join(' ');

const rowClass = [
  'flex flex-row items-center gap-[13px]',
  '[&>svg]:w-[15px] [&>svg]:h-[18px] [&>svg]:fill-muted-foreground',
  '[&>p]:m-0 [&>p]:text-sm [&>p]:font-medium [&>p]:text-muted-foreground',
  'xl:[&>p]:text-base',
].join(' ');

interface Props {
  resort: IFeaturedResort | null;
  regionResorts?: Array<IResortPathInfo>;
  resortDescriptionTabs?: Array<IDescriptionsResort>;
  isOnRegionSkiResorts?: boolean;
  isOnRegionExceptSkiResorts?: boolean;
  utmCampaign: string;
}

export const FeaturedResort = ({
  resort, regionResorts, resortDescriptionTabs, isOnRegionSkiResorts = false, isOnRegionExceptSkiResorts = false, utmCampaign,
}: Props) => {
  const { t } = useOTSTranslation('common');

  if (!resort) {
    return null;
  }

  const resortUuid = resort.uuid?.toString();
  const resortPathInfo = resortUuid && regionResorts
    ? regionResorts.find((item) => item.uuid === resortUuid)
    : undefined;

  if (!resortPathInfo) {
    return null;
  }

  const locationParts = [
    resort.region?.title,
    resort.region?.country,
  ].filter(Boolean) as Array<string>;

  const title = resort.title || t('homePage.featuredResort');
  const location = locationParts.length ? locationParts.join(', ').toUpperCase() : null;

  const resortProfileTab = resortDescriptionTabs?.filter((item) => item.tab === 'resort_profile');
  let description = resortProfileTab && resortProfileTab.length > 0 ? resortProfileTab[0].description : null;

  if (description) {
    description = description.replace(/(<([^>]+)>)/gi, '');

    if (description.length > 250) {
      description = `${description.substring(0, 250)}...`;
    }
  }

  const baseHref = Routes.resortTripPlanningOverview(resortPathInfo);
  const utmParams = new URLSearchParams({
    utm_source: 'onthesnow',
    utm_medium: 'featured_resort',
    utm_campaign: utmCampaign,
    utm_content: resortPathInfo.uuid,
  });
  const linkHref = utmCampaign ? `${baseHref}?${utmParams.toString()}` : baseHref;

  return (
    <section
      id="featured-resort"
      className={cn(
        sectionBaseClass,
        isOnRegionSkiResorts && sectionRegionSkiResortsClass,
        isOnRegionExceptSkiResorts && sectionRegionExceptSkiResortsClass,
      )}
    >
      <div>
        <div className={imageWrapClass}>
          <ImageComponent alt={resort.title} src={resort.image ?? ''} />
          {resort.isPartner && (
            <div className={partnerBadgeClass}>
              <p>{t('common.premierPartner').toUpperCase()}</p>
            </div>
          )}
        </div>

        <div className={snowflakeClass}>
          <SvgSnowflake width="100%" height="100%" viewBox="0 0 176 193" />
        </div>
      </div>

      <div className={contentClass}>
        <h6>{t('homePage.featuredResort')}</h6>
        <h3>{title}</h3>
        {location && (
          <div className={rowClass}>
            <SvgMarker />
            <p>{location}</p>
          </div>
        )}
        {description?.trim() && description !== null ? (
          // eslint-disable-next-line react/no-danger
          <p dangerouslySetInnerHTML={{ __html: description }} />
        ) : (
          <p>{t('home.featuredResort.noDescription')}</p>
        )}
        <Button asChild>
          <a href={linkHref}>
            {t('common.seeResortGuide')}
            <ChevronRight />
          </a>
        </Button>
      </div>
    </section>
  );
};
